import { DrawerBody, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { useAllRecentMeshRuns } from '../../hooks/useRecentMeshRuns';
import { UploadDrawerHeader } from './DrawerHeader';
import { MeshSelect } from './MeshSelect';
import { PipelineSelectorPane } from './PipelineSelectorPane';
import { UploadContainer } from './UploadContainer';
import { UploadDrawerSkeletonContent } from './UploadDrawerShell';
import { useLatestMeshDocuments } from 'packages/dataPipeline/hooks/useLatestMeshDocuments';
import { AssignmentActions } from 'packages/dataPipeline/ui/uploads/actions/AssignmentActions';
import { FundingRequestActions } from 'packages/dataPipeline/ui/uploads/actions/FundingRequestActions';
import { DrawerProgressBar } from 'packages/dataPipeline/ui/uploads/DrawerProgressBar';
import { PipelinesPageLink } from 'packages/dataPipeline/ui/uploads/PipelineDetailsPageLink';
import { DateAndVersionSelector } from 'packages/dataPipeline/ui/uploads/pipelinePicker/DateAndVersionSelector';
import { UploadContextProvider } from '~/packages/dataPipeline/hooks/UploadContext';
import {
  MeshRunParams,
  RDFV,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

type DrawerState =
  | {
      // drawer is showing the select pipeline tab
      state: 'selectMeshRun';
    }
  | {
      // drawer is showing upload tab for new mesh run
      state: 'uploadNew';
    }
  | {
      // drawer is showing upload tab for existing mesh run
      state: 'uploadExisting';
      selectedMesh: SelectedMeshRun;
    };

const useDrawerState = (
  meshRunId: DataPipeline.MeshRunId | null,
  meshRunParams: MeshRunParams | null,
): [DrawerState, React.Dispatch<React.SetStateAction<DrawerState>>] => {
  const defaultDrawerState: DrawerState = useMemo(() => {
    if (meshRunId) {
      finleyAssert(
        meshRunParams,
        'UploadDrawerInternal:meshRunParams must be provided when meshRunId is provided',
      );
      return {
        state: 'uploadExisting',
        selectedMesh: {
          meshRunId,
          meshRunParams,
        },
      };
    }
    if (meshRunParams) {
      return { state: 'uploadNew' };
    }
    return { state: 'selectMeshRun' };
  }, [meshRunId, meshRunParams]);

  return useState<DrawerState>(defaultDrawerState);
};

/* pipeline drawer lets you choose an existing pipeline or start a new one */
const UploadDrawerContentInternal = ({
  organizationId,
  meshRunId,
  meshDocument,
  rdfv,
  latestMeshDocuments,
  allRecentMeshRuns,
  onClose,
  refetchMeshRuns,
}: {
  organizationId: string;
  meshRunId: DataPipeline.MeshRunId | null;
  meshDocument: DataPipeline.MeshDocument | null;
  rdfv: RDFV | null;
  latestMeshDocuments: DataPipeline.MeshDocument[];
  allRecentMeshRuns: DataPipeline.MeshRunRecord[];
  onClose: () => void;
  refetchMeshRuns: () => void;
}) => {
  const meshRunParams: MeshRunParams | null =
    meshDocument && rdfv
      ? {
          meshDocument,
          rdfv,
        }
      : null;

  // upload drawer lets you choose mesh to upload against
  // when selectedMesh is provided, we restrict the choice to that mesh
  const [
    selectedMeshDocument,
    setSelectedMeshDocument,
  ] = useState<DataPipeline.MeshDocument | null>(meshDocument ?? null);
  const [selectedRDFV, setSelectedRDFV] = useState<RDFV | null>(rdfv ?? null);

  // when providing choice of mesh (credit facilities) to choose from
  // we restrict the choice if a mesh is already selected
  const meshDocumentsToChooseFrom = useMemo(() => {
    return meshDocument ? [meshDocument] : latestMeshDocuments;
  }, [latestMeshDocuments, meshDocument]);

  const firstMeshDocument = meshDocumentsToChooseFrom[0] || null;

  useEffect(() => {
    // once we retrieve meshDocumentsToChooseFrom, we should
    // set the 1st one as selected by default
    if (firstMeshDocument && !selectedMeshDocument) {
      setSelectedMeshDocument(firstMeshDocument);
    }
  }, [firstMeshDocument, selectedMeshDocument]);

  useEffect(() => {
    // reset RDFV when mesh document changes
    if (!rdfv) {
      setSelectedRDFV(null);
    }
  }, [rdfv, selectedMeshDocument]);

  // filter mesh runs to only show the ones for the selected mesh document
  const recentMeshRuns = selectedMeshDocument
    ? allRecentMeshRuns.filter(
        (meshRecord) =>
          meshRecord.meshDocumentId === selectedMeshDocument.meshDocumentId,
      )
    : [];

  const selectedMeshParams: MeshRunParams | null =
    selectedMeshDocument && selectedRDFV
      ? {
          meshDocument: selectedMeshDocument,
          rdfv: selectedRDFV,
        }
      : null;

  const [drawerState, setDrawerState] = useDrawerState(
    meshRunId,
    meshRunParams,
  );

  // are we asked to start with a specific (new or existing) upload?
  const showNewReportButton = !meshRunParams;
  const showLatestPipelineButton = !meshRunParams;
  const showUploadTab = ['uploadNew', 'uploadExisting'].includes(
    drawerState.state,
  );

  // we want to show back button when we are showing upload tab for existing mesh run
  // however, we don't want to show back button when UploadDrawer is opened for a specific pipeline
  // from pipeline. (pipeline details page or verification flow )
  const showBackButton =
    drawerState.state === 'uploadExisting' && !meshRunParams;

  const onShowLatestPipelines = () => {
    refetchMeshRuns();
    setDrawerState({ state: 'selectMeshRun' });
  };

  const onNewReport = () => {
    setDrawerState({ state: 'uploadNew' });
    setSelectedRDFV(null);
  };

  const hasInitializedCorrectTab = useRef(false);

  // switch to new upload tab if there are no recent mesh runs
  const numRecentRuns = recentMeshRuns.length;
  useEffect(() => {
    if (
      drawerState.state === 'selectMeshRun' &&
      selectedMeshDocument &&
      numRecentRuns === 0
    ) {
      if (hasInitializedCorrectTab.current) {
        // we run this hook only once
        // to switch to new tab if there are no recent mesh runs.
        // if user chooses to go back to to select mesh run tab, we don't want to switch back
        // we will fetch the latest runs when user goes back to select mesh run tab
        return;
      }

      setDrawerState({ state: 'uploadNew' });
      hasInitializedCorrectTab.current = true;
    }
  }, [drawerState.state, numRecentRuns, selectedMeshDocument, setDrawerState]);

  // you cannot select mesh, if its provided as prop
  // also you cannot also change mesh is date is already entered.
  const allowMeshSelection = !meshRunParams && !selectedRDFV;
  return (
    <>
      <UploadDrawerHeader
        showBackButton={showBackButton}
        onBack={onShowLatestPipelines}
        onClose={onClose}
        onNewReport={showNewReportButton ? onNewReport : undefined}
        onShowLatestPipelines={
          showLatestPipelineButton ? onShowLatestPipelines : undefined
        }
      />
      <DrawerBody>
        <VStack spacing="20px" alignItems="flex-start">
          <>
            {/* select credit facility/mesh */}
            <MeshSelect
              allowSelection={allowMeshSelection}
              selectedMeshDocumentId={
                selectedMeshDocument?.meshDocumentId ?? null
              }
              onMeshSelected={setSelectedMeshDocument}
              meshDocuments={meshDocumentsToChooseFrom}
            />
            {showUploadTab && selectedMeshDocument && (
              <DateAndVersionSelector
                // you can't change date and version once selected
                // to reset the selection, you need to click "New Report"
                allowSelection={!selectedRDFV}
                borrowerOrganizationId={
                  selectedMeshDocument.borrowerOrganizationId
                }
                selectedRDFV={selectedRDFV}
                setSelectedRDFV={setSelectedRDFV}
              />
            )}

            {showUploadTab && selectedMeshParams && (
              // show upload tab only when we have selected mesh params
              <UploadContextProvider
                organizationId={organizationId}
                meshRunId={null}
                meshRunParams={selectedMeshParams}
              >
                <UploadContainer closeDrawer={onClose} />
                <AssignmentActions />
                <FundingRequestActions />
              </UploadContextProvider>
            )}
            {!showUploadTab && selectedMeshDocument && (
              // show pipeline selector only when we are in selectMeshRun tab
              <PipelineSelectorPane
                recentMeshRuns={recentMeshRuns}
                onPipelineSelected={(meshRunRecord) => {
                  setSelectedRDFV({
                    reportingDate: meshRunRecord.reportingDate,
                    fileVersion: meshRunRecord.fileVersion,
                  });
                  setDrawerState({
                    state: 'uploadExisting',
                    selectedMesh: {
                      meshRunId: meshRunRecord.meshRunId,
                      meshRunParams: {
                        meshDocument: selectedMeshDocument,
                        rdfv: {
                          reportingDate: meshRunRecord.reportingDate,
                          fileVersion: meshRunRecord.fileVersion,
                        },
                      },
                    },
                  });
                }}
              >
                <PipelinesPageLink onLinkClick={onClose} />
              </PipelineSelectorPane>
            )}
          </>
        </VStack>
      </DrawerBody>
      {showUploadTab && selectedMeshParams && (
        // show progress bar only when upload tab is active
        <UploadContextProvider
          organizationId={organizationId}
          meshRunId={null}
          meshRunParams={selectedMeshParams}
        >
          <DrawerProgressBar />
        </UploadContextProvider>
      )}
    </>
  );
};

/**
 *  This control fetches and renders the contents of the drawer data
 *  and passes on to the UploadDrawerInternal for rendering it.
 *  The caller is responsible for putting it inside the shell.
 *  We do this to enable caller to render skeleton insider the drawer
 *  while the data is loading
 */
export const UploadDrawerContent = ({
  organizationId,
  onClose,
  meshRunId,
  meshDocument,
  rdfv,
}: {
  organizationId: string;
  onClose: () => void;
  meshRunId: DataPipeline.MeshRunId | null;
  meshDocument: DataPipeline.MeshDocument | null;
  rdfv: RDFV | null;
}) => {
  const {
    latestMeshDocuments,
    loading: loadingLatestMeshDocuments,
  } = useLatestMeshDocuments({
    organizationId,
  });

  const {
    recentMeshRuns,
    loading: loadingRecentMeshRuns,
    refetch: refetchMeshRuns,
  } = useAllRecentMeshRuns({
    organizationId,
  });

  if (loadingLatestMeshDocuments || loadingRecentMeshRuns) {
    return <UploadDrawerSkeletonContent onClose={onClose} />;
  }

  // after fetching the data, we render the actual content
  return (
    <UploadDrawerContentInternal
      organizationId={organizationId}
      onClose={onClose}
      meshRunId={meshRunId}
      meshDocument={meshDocument}
      rdfv={rdfv}
      allRecentMeshRuns={recentMeshRuns}
      latestMeshDocuments={latestMeshDocuments}
      refetchMeshRuns={refetchMeshRuns}
    />
  );
};
